import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AsyncDialog, AsyncDialogRef } from '../../../../components/AsyncDialog/AsyncDialog';
import {
  ConfirmationDialog,
  ConfirmationDialogData,
} from '../../../../components/ConfirmationDialog/ConfirmationDialog';
import { TableCard } from '../../../../components/Section';
import { RtkQueryTable, RtkQueryTableModels } from '../../../../components/Table/RtkQueryTable';
import {
  TREASURY_FIRST_PAGE,
  TREASURY_GRID_HEIGHT,
  TREASURY_INSUFFICIENT_FUNDS_ACTIONS,
  TREASURY_LIST_PAGE_SIZE,
} from '../../../../constants/treasury';
import { usePermissions } from '../../../../hooks';
import {
  useCreateTreasuryRedeemAutomationMutation,
  useDeleteTreasuryRedeemAutomationMutation,
  useFetchTreasuryRedeemAutomationsQuery,
  usePauseTreasuryRedeemAutomationMutation,
  useResumeTreasuryRedeemAutomationMutation,
  useUpdateTreasuryRedeemAutomationMutation,
} from '../../../../redux/features/treasury/treasury.apiSlice';
import { SubscribedPortfolio } from '../../../../types/subscribedPortfolio';
import { SaveTreasuryRedeemAutomationRequest, TreasuryRedeemAutomation } from '../../../../types/treasury';
import * as permissions from '../../../../utils/permissions';
import { getTreasuryErrorMessage, getTreasuryRedeemScheduleLabel } from '../../../../utils/treasury';
import { TreasuryRedeemRuleDialog } from './TreasuryRedeemRuleDialog';
import { TreasuryRedeemRunsDialog } from './TreasuryRedeemRunsDialog';

type TreasuryRedeemRulesTabProps = {
  companyId: number;
  portfolios: SubscribedPortfolio[];
  isPortfoliosLoading: boolean;
};

const statusChip = (rule: TreasuryRedeemAutomation) => {
  if (rule.needManualReview) {
    return <Chip size="small" color="error" label="Needs review" />;
  }

  return (
    <Chip size="small" color={rule.isActive ? 'success' : 'default'} label={rule.isActive ? 'Active' : 'Paused'} />
  );
};

/**
 * Redemption rules — redeem a fixed amount from a treasury portfolio on a schedule. One active rule per portfolio.
 * A rule fails up to 3 times, then waits for manual review; editing or resuming it clears that.
 */
export const TreasuryRedeemRulesTab = ({ companyId, portfolios, isPortfoliosLoading }: TreasuryRedeemRulesTabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const canCreate = usePermissions({ items: [permissions.Treasury.createRedeemRule] });
  const canUpdate = usePermissions({ items: [permissions.Treasury.updateRedeemRule] });
  const canDelete = usePermissions({ items: [permissions.Treasury.deleteRedeemRule] });
  const confirmRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const [dialogRule, setDialogRule] = useState<TreasuryRedeemAutomation | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [runsRuleId, setRunsRuleId] = useState<number | null>(null);
  const [busyRuleId, setBusyRuleId] = useState<number | null>(null);
  // thriftplan answers every rule at once, so the grid pages them itself.
  const [tableModels, setTableModels] = useState<RtkQueryTableModels>({ paginationModel: TREASURY_FIRST_PAGE });
  const { data: rules, isFetching, isError, error, refetch } = useFetchTreasuryRedeemAutomationsQuery(companyId);
  const [createRule, { isLoading: isCreating }] = useCreateTreasuryRedeemAutomationMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateTreasuryRedeemAutomationMutation();
  const [pauseRule] = usePauseTreasuryRedeemAutomationMutation();
  const [resumeRule] = useResumeTreasuryRedeemAutomationMutation();
  const [deleteRule] = useDeleteTreasuryRedeemAutomationMutation();

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to load the redemption rules'), { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  const openDialog = useCallback((rule: TreasuryRedeemAutomation | null) => {
    setDialogRule(rule);
    setIsDialogOpen(true);
  }, []);

  const save = async ({ externalSubscribedPortfolioId, ...rest }: SaveTreasuryRedeemAutomationRequest) => {
    try {
      if (dialogRule) {
        await updateRule({ companyId, id: dialogRule.id, payload: rest }).unwrap();
        enqueueSnackbar('Rule saved', { variant: 'success' });
      } else {
        await createRule({ companyId, payload: { externalSubscribedPortfolioId, ...rest } }).unwrap();
        enqueueSnackbar('Rule created', { variant: 'success' });
      }

      setIsDialogOpen(false);
    } catch (saveError) {
      enqueueSnackbar(getTreasuryErrorMessage(saveError, 'Failed to save the rule'), { variant: 'error' });
    }
  };

  const columns = useMemo<GridColDef<TreasuryRedeemAutomation>[]>(() => {
    const togglePause = async (rule: TreasuryRedeemAutomation) => {
      setBusyRuleId(rule.id);

      try {
        if (rule.isActive) {
          await pauseRule({ companyId, id: rule.id }).unwrap();
          enqueueSnackbar('Rule paused', { variant: 'success' });
        } else {
          await resumeRule({ companyId, id: rule.id }).unwrap();
          enqueueSnackbar('Rule resumed — it starts again from today', { variant: 'success' });
        }
      } catch (toggleError) {
        enqueueSnackbar(getTreasuryErrorMessage(toggleError, 'Failed to update the rule'), { variant: 'error' });
      } finally {
        setBusyRuleId(null);
      }
    };

    const remove = (rule: TreasuryRedeemAutomation) =>
      confirmRef.current?.show({
        title: 'Delete Rule',
        description: `Delete rule #${rule.id}? It stops firing; its past runs stay.`,
        acceptButtonText: 'Delete',
        rejectButtonText: 'Cancel',
        confirmButtonColor: 'error',
        onConfirm: async () => {
          try {
            await deleteRule({ companyId, id: rule.id }).unwrap();
            enqueueSnackbar('Rule deleted', { variant: 'success' });
          } catch (deleteError) {
            enqueueSnackbar(getTreasuryErrorMessage(deleteError, 'Failed to delete the rule'), { variant: 'error' });
          }
        },
      });

    return [
      { field: 'id', headerName: 'ID', width: 80, sortable: false },
      {
        field: 'externalSubscribedPortfolioId',
        headerName: 'Portfolio',
        width: 110,
        sortable: false,
        valueFormatter: ({ value }) => `#${value}`,
      },
      { field: 'amount', headerName: 'Amount (SAR)', width: 130, sortable: false },
      {
        field: 'frequency',
        headerName: 'Schedule',
        width: 200,
        sortable: false,
        valueGetter: ({ row }) => getTreasuryRedeemScheduleLabel(row),
      },
      {
        field: 'insufficientFundsAction',
        headerName: 'If Short',
        width: 160,
        sortable: false,
        valueGetter: ({ row }) =>
          TREASURY_INSUFFICIENT_FUNDS_ACTIONS.find((option) => option.value === row.insufficientFundsAction)?.label ??
          row.insufficientFundsAction,
      },
      {
        field: 'isActive',
        headerName: 'Status',
        width: 130,
        sortable: false,
        renderCell: ({ row }) => statusChip(row),
      },
      {
        field: 'nextRunAt',
        headerName: 'Next Run',
        width: 120,
        sortable: false,
        valueGetter: ({ row }) => (row.isActive ? moment(row.nextRunAt).format('MM/DD/YYYY') : '—'),
      },
      {
        field: 'lastError',
        headerName: 'Last Error',
        width: 220,
        sortable: false,
        valueGetter: ({ row }) => (row.lastError ? `${row.lastError} (retry ${row.retry})` : '—'),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <>
            <Tooltip title="Runs">
              <IconButton size="small" onClick={() => setRunsRuleId(row.id)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <span>
                <IconButton size="small" disabled={!canUpdate} onClick={() => openDialog(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.isActive ? 'Pause' : 'Resume'}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canUpdate || busyRuleId === row.id}
                  onClick={() => togglePause(row)}
                >
                  {row.isActive ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton size="small" color="error" disabled={!canDelete} onClick={() => remove(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        ),
      },
    ];
  }, [companyId, canUpdate, canDelete, busyRuleId, openDialog, pauseRule, resumeRule, deleteRule, enqueueSnackbar]);

  return (
    <>
      <TableCard
        title="Redemption Rules"
        description="One active rule per portfolio. A paused rule restarts from today when resumed."
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={refetch}>
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!canCreate}
              onClick={() => openDialog(null)}
            >
              New Rule
            </Button>
          </Stack>
        }
      >
        <RtkQueryTable
          models={tableModels}
          onModelsChange={setTableModels}
          rows={rules ?? []}
          columns={columns}
          loading={isFetching}
          paginationMode="client"
          pageSizeOptions={[TREASURY_LIST_PAGE_SIZE]}
          disableColumnFilter
          wrapperHeight={TREASURY_GRID_HEIGHT}
        />
      </TableCard>

      <TreasuryRedeemRuleDialog
        open={isDialogOpen}
        rule={dialogRule}
        portfolios={portfolios}
        isPortfoliosLoading={isPortfoliosLoading}
        isSaving={isCreating || isUpdating}
        onClose={() => setIsDialogOpen(false)}
        onSave={save}
      />
      <TreasuryRedeemRunsDialog companyId={companyId} ruleId={runsRuleId} onClose={() => setRunsRuleId(null)} />
      <AsyncDialog ref={confirmRef} dialog={ConfirmationDialog} />
    </>
  );
};
